import PropTypes from 'prop-types';
import React, { Component, createRef } from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import _ from 'lodash';

// Action Creators
import { addContactToGroup } from 'actions/Contact/Group';
import { assignCareCoordinator } from 'actions/CareCoordinator/Contact/Group';
import { setDashboardRefetch } from 'actions/Dashboard';
import { fetchClientRelationship } from 'src/actions/CareCoordinator/Contact/Group/fetchClientRelationship';

// Utilities
import labelCustomization from 'src/common/utils/Customization/labels';
import defaultLabels from 'src/constants/labels';
import { hbh1509ClientsDrawer } from 'src/common/utils/FeatureFlags/flags';

// Components
import { Dialog } from '@unite-us/ui';
import { PortalizedDialog, BarLoader } from '@unite-us/app-components';
import IconButton from 'common/buttons/IconButton/IconButton';
import Assign from './components/Assign';
import CareCoordinatorInfo from './components/CareCoordinatorInfo';
import EditCareCoordinatorForm from './components/EditCareCoordinatorForm';
import './stylesheets/care-coordinator.scss';

class CareCoordinator extends Component {
  constructor(props) {
    super(props);
    this.careCoordinatorModal = createRef();
    this.showModal = this.showModal.bind(this);
    this.handleDialogClose = this.handleDialogClose.bind(this);
    this.onModalConfirmation = this.onModalConfirmation.bind(this);
    this.getClientRelationship = this.getClientRelationship.bind(this);
    this.state = {
      clientRelationship: {},
      isFetching: false,
      isDialogOpen: false,
    };
  }

  componentDidMount() {
    this.getClientRelationship();
  }

  handleDialogClose() {
    if (this.careCoordinatorModal.current) {
      this.careCoordinatorModal.current.closeDialog();
    }
    this.setState({ isDialogOpen: false });
  }

  onModalConfirmation(formData) {
    const { care_coordinator: careCoordinator } = formData;
    const { clientRelationship } = this.state;
    const clientRelationshipId = _.get(clientRelationship, 'id', null);
    const { isCC, groupId, contact: { id: contactId } } = this.props;
    const msg = `${this.props.labels.CareCoordinator} assigned.`;

    return this.props.assignCareCoordinator(clientRelationshipId, careCoordinator, isCC, contactId, groupId, msg)
      .then((response) => {
        this.getClientRelationship();
        this.props.setDashboardRefetch();
        this.handleDialogClose();
        return response;
      });
  }

  async getClientRelationship() {
    const {
      groupId,
      contact: { id: contactId },
    } = this.props;
    this.setState({ isFetching: true });
    const clientRelationship = await fetchClientRelationship(contactId, groupId);
    this.setState({
      clientRelationship,
      isFetching: false,
    });
  }

  showModal() {
    this.setState({ isDialogOpen: true }, () => {
      setTimeout(() => {
        if (this.careCoordinatorModal.current) {
          this.careCoordinatorModal.current.openDialog();
        }
      }, 0);
    });
  }

  renderDialog() {
    const {
      detailObj, getClientRelationship, labels,
    } = this.props;
    const { clientRelationship } = this.state;
    return (
      <Dialog
        id="care-coordinator-modal"
        ref={this.careCoordinatorModal}
        title={`Assign ${labels.CareCoordinator}`}
        size="normal"
        onRequestClose={this.handleDialogClose}
      >
        <EditCareCoordinatorForm
          detailObj={detailObj}
          coordinator={clientRelationship?.care_coordinator}
          onCancel={this.handleDialogClose}
          onFormSubmit={this.onModalConfirmation}
          getClientRelationship={getClientRelationship}
        />
      </Dialog>
    );
  }

  render() {
    const { clientRelationship, isFetching, isDialogOpen } = this.state;
    const {
      showCCDetails, className, labels, hbh1509ClientsDrawerEnabled,
    } = this.props;
    const hasCareCoordinator = !!_.get(clientRelationship, 'care_coordinator');

    const shouldPortal = isDialogOpen && hbh1509ClientsDrawerEnabled;

    return (
      <div className={classNames(className, 'care-coordinator')}>
        <div className="flex items-center justify-between mb-1">
          <h3>{labels.CareCoordinator}</h3>
          {!isFetching && hasCareCoordinator && (
            <IconButton
              className="care-coordinator__edit-btn"
              labelText="Edit"
              icon="IconPencil"
              onClick={this.showModal}
            />
          )}
        </div>
        {isFetching && <BarLoader className="h-38px" size="full" />}
        {!isFetching && hasCareCoordinator ? (
          <CareCoordinatorInfo
            careCoordinator={clientRelationship?.care_coordinator}
            showDetails={showCCDetails}
          />
        ) : (
          <Assign showModal={this.showModal} />
        )}
        <PortalizedDialog shouldPortal={shouldPortal}>
          {this.renderDialog()}
        </PortalizedDialog>
      </div>
    );
  }
}

CareCoordinator.propTypes = {
  className: PropTypes.string,
  assignCareCoordinator: PropTypes.func.isRequired,
  contact: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  groupId: PropTypes.string.isRequired,
  setDashboardRefetch: PropTypes.func.isRequired,
  detailObj: PropTypes.object.isRequired,
  showCCDetails: PropTypes.bool,
  isCC: PropTypes.bool.isRequired,
  getClientRelationship: PropTypes.func.isRequired,
  labels: PropTypes.object,
  hbh1509ClientsDrawerEnabled: PropTypes.bool.isRequired,
};

CareCoordinator.defaultProps = {
  className: '',
  showCCDetails: false,
  labels: defaultLabels,
};

function mapStateToProps(state) {
  const { groupId } = state.session;
  const currentGroup = _.get(state, 'globalState.currentProvider.group') || {};

  return {
    isCC: _.get(currentGroup, 'provider_type') === 'coordination_center',
    groupId,
    labels: labelCustomization(state),
    hbh1509ClientsDrawerEnabled: hbh1509ClientsDrawer(state),
  };
}

export default connect(mapStateToProps, {
  addContactToGroup,
  assignCareCoordinator,
  setDashboardRefetch,
})(CareCoordinator);
